// #region imports

// --- Angular
import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse
} from '@angular/common/http';

// --- RxJs
import { of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

// --- HS Code
import { AppService } from '../app.service';
import { RegistrationBaseService } from './registration-base.service';
import {
  ApplicationError,
  RegistrationModel,
  RegistrationResult,
  EmailAddressValidationModel,
  ResolvedTokenModel,
  ToastModel
} from '../../../models';
import { environment } from '../../../../environments/environment';
import { AppToastService } from '../app-toast.service';

// #endregion

@Injectable({
  providedIn: 'root'
})
export class RegistrationService extends RegistrationBaseService {

  constructor(
    private httpClient: HttpClient,
    private appService: AppService,
    private translate: TranslateService,
    private toastService: AppToastService
  ) {
    super();
  }

  clearState(): void {
    this._isEmailAddressInUse$.next({
      isValidEmailAddress: false,
      isEmailAddressValidated: false,
      emailAddressExists: false
    } as EmailAddressValidationModel);
    this._registrationResolutionResult$.next(null);
    this._registrationResult$.next(null);
    this._emailAddress$.next(null);
    this._isRegistering$.next(false);
    this._isRegistered$.next(false);
    this._isValidatingEmailAddress$.next(false);
    this._registrationError$.next(null);
  }

  registerNewUser(registration: RegistrationModel): void {
    this._registrationError$.next(null);

    this._isRegistering$.next(true);
    const registrationUrl =
      environment.apiConfig.baseUrl + environment.apiConfig.registerApi;
    this.appService.setLoading(true);
    this.httpClient
      .get(environment.apiConfig.baseUrl + environment.apiConfig.antiForgeryTokenApi).pipe(
        switchMap(() => {
          return this.httpClient.post<RegistrationResult>(registrationUrl, registration)
          .pipe(catchError(this.handleError.bind(this)));
        }),
        catchError(this.handleError.bind(this))
      ).subscribe((registrationResult: RegistrationResult) => {
        if (registrationResult.errorMessage.includes('ISBN')) {
          registrationResult.validationMessages = {IsbnNumber: registrationResult.errorMessage};
        }
        if (registrationResult.errorMessage.includes('email')) {
          registrationResult.validationMessages = {EmailAddress: registrationResult.errorMessage};
        }
        this._isRegistering$.next(false);
        this._isRegistered$.next(registrationResult.isRegistered);
        this.appService.setLoading(false);
        this._registrationResult$.next(registrationResult);
        this._emailAddress$.next(registration.emailAddress);
        this._registrationError$.next(registrationResult.errorMessage);
      });
  }

  checkIfEmailAddressInUse(emailAddress: string): void {
    if (!emailAddress) {
      return;
    }
    const uri =
      environment.apiConfig.baseUrl +
      environment.apiConfig.checkEmailAddressApi +
      `?emailAddress=${encodeURIComponent(emailAddress)}`;
    this._isValidatingEmailAddress$.next(true);

    this.httpClient
      .get<EmailAddressValidationModel>(uri)
      .pipe(catchError(this.handleError.bind(this)))
      .subscribe((result: EmailAddressValidationModel) => {
        this._isEmailAddressInUse$.next(result);
        this._isValidatingEmailAddress$.next(false);
      });
  }

  resendRegistrationEmail(emailAddress: string) {
    if (!emailAddress) {
      return;
    }
    const uri =
      environment.apiConfig.baseUrl +
      environment.apiConfig.resendRegistrationValidationEmailApi +
      `?emailAddress=${encodeURIComponent(emailAddress)}`;
    this.appService.setLoading(true);
    this.httpClient
      .get(uri)
      .pipe(catchError(this.handleError.bind(this)))
      .subscribe(() => {
        this.toastService.show({
          autoHide: true,
          delay: 3000,
          class: 'bg-success text-light',
          header: this.translate.instant('emailResentHeader'),
          body: this.translate.instant('emailResentBody')
        } as ToastModel);
        
        this.appService.setLoading(false);
      });
  }

  private handleError(err) {
    this.appService.setLoading(false);
    this._isValidatingEmailAddress$.next(false);
    this._isRegistering$.next(false);

    console.error(err);

    let error: ApplicationError;
    if (err && err instanceof HttpErrorResponse) {
      // A client-side or network error occurred. Handle it accordingly.
      let detailedMessage = `${err.message}`;
      if (err.statusText) {
        detailedMessage += `<br/>${err.statusText}`;
      }
      error = {
        errorMessage: this.translate.instant('errorMessageRegistration'),
        errorNumber: err.status,
        detailedMessage
      } as ApplicationError;
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      error = {
        errorMessage: this.translate.instant('errorMessageRegistration'),
        originalError: err
      } as ApplicationError;
    }
    this.appService.setLastError(error);

    return of({ isRegistered: false } as RegistrationResult);
  }
}
